import PropTypes from "prop-types";

/**
 * Variant PropTypes
 * Reusable PropTypes for common style variants and visual states used across components
 */

/**
 * PropType for component size variants
 * Standard size options used across buttons, inputs, and other components
 * @type {PropTypes.Requireable<'small' | 'default' | 'medium' | 'large'>}
 */
export const sizePropType = PropTypes.oneOf(["small", "default", "medium", "large"]);

/**
 * PropType for component appearance/color variants
 * Standard visual style options matching design system
 * @type {PropTypes.Requireable<
 *  'default' |
 *  'primary' |
 *  'primary-elevated' |
 *  'primary-outline' |
 *  'secondary' |
 *  'success' |
 *  'danger' |
 *  'warning' |
 *  'info'
 * >}
 */
export const appearancePropType = PropTypes.oneOf([
  "default",
  "primary",
  "primary-elevated",
  "primary-outline",
  "secondary",
  "success",
  "danger",
  "warning",
  "info",
]);

/**
 * PropType for component status states
 * Used to represent operational or data states
 * @type {PropTypes.Requireable<'active' | 'inactive' | 'pending' | 'success' | 'error' | 'warning' | 'completed'>}
 */
export const statusPropType = PropTypes.oneOf([
  "active",
  "inactive",
  "pending",
  "success",
  "error",
  "warning",
  "completed",
]);

/**
 * PropType for display type variants
 * Used to control layout display mode (flex or grid)
 * @type {PropTypes.Requireable<'flex' | 'grid'>}
 */
export const displayPropType = PropTypes.oneOf(["flex", "grid"]);

/**
 * PropType for text alignment options
 * Standard text alignment values
 * @type {PropTypes.Requireable<'left' | 'center' | 'right' | 'justify'>}
 */
export const alignPropType = PropTypes.oneOf(["left", "center", "right", "justify"]);

/**
 * PropType for flexbox justify-content options
 * Used for horizontal alignment in flex containers
 * @type {PropTypes.Requireable<'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around' | 'space-evenly'>}
 */
export const justifyPropType = PropTypes.oneOf([
  "flex-start",
  "center",
  "flex-end",
  "space-between",
  "space-around",
  "space-evenly",
]);

/**
 * PropType for component shape variants
 * Used for visual shape modifications (buttons, avatars, etc.)
 * @type {PropTypes.Requireable<'circle' | 'square' | 'rounded'>}
 */
export const shapePropType = PropTypes.oneOf(["circle", "square", "rounded"]);

/**
 * PropType for input type variants
 * Standard HTML input types
 * @type {PropTypes.Requireable<'text' | 'password' | 'email' | 'number' | 'date' | 'time' | 'tel' | 'url' | 'search'>}
 */
export const inputTypePropType = PropTypes.oneOf([
  "text",
  "password",
  "email",
  "number",
  "date",
  "time",
  "tel",
  "url",
  "search",
]);

/**
 * PropType for validation state
 * Used to indicate field validation status
 * Can be boolean or string representation
 * @type {PropTypes.Requireable<boolean | string>}
 */
export const validationPropType = PropTypes.oneOfType([PropTypes.bool, PropTypes.string]);
